import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader, Check, XCircle } from "lucide-react";
import api from "@/lib/api";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [ref, setRef] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      if (cancelled) return;
      attempt += 1; setTries(attempt);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); setRef(data.ref); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus(data.payment_status); return; }
      } catch { /* keep trying */ }
      if (attempt >= 20) { setStatus("timeout"); return; }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center p-8" style={{ background: "var(--cream)" }} data-testid="payment-return">
      <div className="at-card p-10 max-w-xl w-full text-center">
        {status === "polling" && (
          <>
            <Loader size={40} className="mx-auto animate-spin" style={{ color: "var(--kiwi)" }} />
            <div className="mt-4 font-display font-black text-2xl uppercase">Confirming your payment…</div>
            <div className="mt-2 font-serif italic" style={{ color: "var(--muted)" }}>This usually takes a couple of seconds. Try {tries} of 20.</div>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--kiwi)", color: "var(--cream-2)" }}>
              <Check size={32} strokeWidth={3} />
            </div>
            <div className="mt-6 font-display font-black text-3xl uppercase">Payment confirmed</div>
            <div className="mt-3 font-serif italic text-lg" style={{ color: "var(--ink-2)" }}>
              Your question {ref ? <>(<b>{ref}</b>)</> : null} is with your tax agent. You'll see the answer in your portal within one business day.
            </div>
            <button data-testid="return-view-questions" onClick={() => nav("/portal/questions")} className="at-btn mt-8">View my questions</button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle size={48} className="mx-auto" style={{ color: "var(--clay)" }} />
            <div className="mt-4 font-display font-black text-2xl uppercase">Payment {status}</div>
            <div className="mt-2 font-serif italic" style={{ color: "var(--muted)" }}>Nothing was charged. Give it another go from the portal.</div>
            <button onClick={() => nav("/portal")} className="at-btn ghost mt-6">Back to portal</button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader size={40} className="mx-auto" style={{ color: "var(--muted)" }} />
            <div className="mt-4 font-display font-black text-2xl uppercase">Still processing</div>
            <div className="mt-2 font-serif italic" style={{ color: "var(--muted)" }}>Your bank is taking longer than usual. Your question will appear in your portal as soon as Stripe confirms — safe to check back in a minute.</div>
            <button onClick={() => nav("/portal/questions")} className="at-btn mt-6">Go to my questions</button>
          </>
        )}
      </div>
    </div>
  );
}
